import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { NoSearchResultsDisplay } from '@/components/ui/NoSeachResultsDisplay'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { AnimatePresence } from 'motion/react'
import { useMemo } from 'react'
import { WorkspacesGridItem } from './WorkspacesGridItem'
import { Skeleton } from '@/components/shadcn/skeleton'
export type WorkspaceStatusTab = 'current' | 'archived'

// Manual type override because the API is not returning the correct type
export type MessagesWorkspacesWorkspaceCard = Omit<RestSchemas['Messages.Workspaces.WorkspaceCard'], 'appTypes'> & {
  app_types: number
  status?: 'Active' | 'Archived' | null
}

export function workspaceMatchesStatusTab(
  status: MessagesWorkspacesWorkspaceCard['status'],
  tab: WorkspaceStatusTab
): boolean {
  if (tab === 'archived') {
    return status === 'Archived'
  }
  return status === 'Active' || status == null
}

export function WorkspacesGrid({
  searchString,
  statusTab,
}: {
  searchString: string
  statusTab: WorkspaceStatusTab
}) {
  const { getWorkspacesApiGetCurrentUserWorkspaces } = useRestClient()
  const { data } = getWorkspacesApiGetCurrentUserWorkspaces.useSuspenseQuery(undefined)
  const currentUserWorkspaces = data as unknown as MessagesWorkspacesWorkspaceCard[]

  const filteredWorkspacesIds = useMemo(
    () =>
      currentUserWorkspaces
        .filter((workspace) => {
          if (!workspaceMatchesStatusTab(workspace.status, statusTab)) return false
          if (!searchString) return true
          const orgSearchMatch = workspace.organisation.toLowerCase().includes(searchString.toLowerCase())
          const projectSearchMatch = workspace.project.toLowerCase().includes(searchString.toLowerCase())
          const appSearchMatch = workspace.application.toLowerCase().includes(searchString.toLowerCase())
          return orgSearchMatch || projectSearchMatch || appSearchMatch
        })
        .map((workspace) => workspace.workspaceId),
    [currentUserWorkspaces, searchString, statusTab]
  )

  return (
    <ScrollArea className="h-full">
      <AnimatePresence>
        {filteredWorkspacesIds.length > 0 ? (
          <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-8 px-3 py-8">
            {currentUserWorkspaces.map((workspace) => (
              <WorkspacesGridItem
                key={workspace.workspaceId}
                workspace={workspace}
                className={filteredWorkspacesIds.includes(workspace.workspaceId) ? '' : 'hidden'}
              />
            ))}
          </div>
        ) : (
          <NoSearchResultsDisplay />
        )}
      </AnimatePresence>
    </ScrollArea>
  )
}

export function WorkspacesGridLoading() {
  return (
    <>
      <ComponentHeader className="border-b-0 pt-5">
        <div className="ml-auto mr-0 flex items-center gap-4">
          <div className="flex items-center gap-x-2">
            <Skeleton className="h-[35px] w-[150px]" />
          </div>
        </div>
      </ComponentHeader>
      <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-8 pb-5">
        {Array.from({ length: 16 }).map((_, index) => (
          <Skeleton key={index} className="h-[192px] w-full rounded-xl" />
        ))}
      </div>
    </>
  )
}
